import { SourceOfLead } from 'next-auth';
import { Document } from '@contentful/rich-text-types';

export interface CustomerInfo {
  sourceOfLead: SourceOfLead;
  firstName: string;
  lastName: string;
  age: string;
  email: string;
  zipCode: string;
  spouse?: Spouse;
  dependents?: Dependent[];
  state: string;
  dob: string;
  startDate: string;
  emailVerified: boolean;
}

export interface Spouse {
  firstName?: string;
  lastName?: string;
  age?: string;
  dob?: string;
}

export interface Dependent {
  id?: string;
  firstName?: string;
  lastName?: string;
  age?: string;
  dob?: string;
}

export interface Plan {
  planCode: PlanCode;
  planName: string;
  monthlyPrice?: number;
  available: boolean;
  annualPrice?: string;
  pricingTier: PricingTier;
  productCode?: ProductCode;
  productType: keyof typeof Products;
  stateCode: string;
  description: Document;
  planDetailsPdf: string;
  limitationsAndExclusions?: string;
}

export interface Plans {
  plans: Plan[];
  loaded: boolean;
}

export interface Quote {
  customer: CustomerInfo;
  product: keyof typeof Products;
  productCode?: ProductCode;
  planCode?: PlanCode;
  monthlyPrice?: number;
  planName?: string;
  pricingTier?: PricingTier;
  planDetailsPdf?: string;
  stateSpecificQuestions?: {};
}

export interface Enrollment {
  paymentMethod?: string;
  loaded?: boolean;
  insureds: Insured[];
  address: Address;
  billingAddress?: Address;
  agreements: Agreement[];
  replacementCoverage: null;
}

export enum Products {
  Dental = 'Dental',
  Accident = 'Accident',
  Vision = 'Vision',
}

export enum DentalPlanName {
  PREMIER = 'Premier',
  DIAMOND = 'Diamond',
  ACHIEVER = 'Achiever',
  CORE = 'Core',
  STARTER = 'Starter',
}

export enum ProductCode {
  PPO = 'PPO',
  PREC_IDPPO = 'PREC-IDPPO',
  DHMO = 'DHMO',
  IVSP = 'IVSP',
}

export enum DentalPlanCode {
  GOLD_ADV_PPO = 'GOLD_ADV_PPO',
  GOLD_SELECT_PPO = 'GOLD_SELECT_PPO',
  SILVER_ADV_PPO = 'SILVER_ADV_PPO',
  SILVER_SELECT_PPO = 'SILVER_SELECT_PPO',
  BRONZE_ADV_PPO = 'BRONZE_ADV_PPO',
  COMPLETE_SELECT_PPO = 'COMPLETE_SELECT_PPO',
}

export enum AccidentPlanCode {
  LOW = 'LOW',
  HIGH = 'HIGH',
}

export enum VisionPlanCode {
  CUSTOM_LOW = 'CustomLow',
  CUSTOM_HIGH = 'CustomHigh',
  STANDARD_120 = 'Standard120',
  STANDARD_150 = 'Standard150',
}

export type PlanCode = DentalPlanCode | VisionPlanCode | AccidentPlanCode | '';

export enum PricingTier {
  IND = 'ind',
  IND_SPS = 'ind_sps',
  IND_CHD = 'ind_chd',
  IND_FML = 'ind_fml',
}

export enum AgreementEnum {
  YES = 'YES',
  NO = 'NO',
}

export enum BillingAndMailingAddressEnum {
  PRIMARY = 'PRIMARY',
  OTHER = 'OTHER',
  PO_BOX = 'PO_BOX',
}

export interface AddressInformationDetails {
  address: string;
  secondaryAddress: string;
  city: string;
  zipCode: string;
}
export interface AlternateHomeDetails {
  nameOfStateOrCountry: string;
  residencyMonthsPerYear: string;
  otherResidencyDetails: AddressInformationDetails;
}

type Nullable<T> = T | null;
export interface NJResidencyQuestionnaire {
  resident?: Nullable<boolean>;
  coveredByMedicare?: Nullable<boolean>;
  otherResidence?: Nullable<boolean>;
  otherResidenceAddress?: Nullable<{
    addressLine1: string;
    addressLine2?: Nullable<string>;
    city: string;
    stateCode: string;
    zipCode: string;
    country: string;
    lengthOfStayAnnually?: Nullable<string>;
  }>;

  otherResidencePhone?: Nullable<{
    phoneNumber?: Nullable<string>;
    type?: Nullable<string>;
    preferred?: Nullable<boolean>;
  }>;

  billingAddress?: Nullable<{
    addressLine1: string;
    addressLine2?: Nullable<string>;
    city: string;
    stateCode: string;
    zipCode: string;
    country?: Nullable<string>;
  }>;

  mailingAddress?: Nullable<{
    addressLine1: string;
    addressLine2?: Nullable<string>;
    city: string;
    stateCode: string;
    zipCode: string;
    country?: Nullable<string>;
  }>;

  eligibleForMedicare?: Nullable<boolean>;
  insuredsCoveredByMedicare?: Nullable<
    Nullable<{ answer: string; name: string }>[]
  >;
  reasonForApplying?: Nullable<string>;
}

export interface ILResidencyQuestionnaire {
  residesInNursingHome: Nullable<boolean>;
  hasMedicalConditionAffectingDecisions: Nullable<boolean>;
}

export interface CoReplacementQuestionnaire {
  hasAnotherHealthPolicy?: Nullable<boolean>;
  otherHealthInsuranceCompany?: Nullable<string>;
  intentToReplaceCurrentCoverage?: Nullable<string>;
  otherAccidentSicknessPolicy?: Nullable<boolean>;
  otherAccidentSicknessPolicyCompany?: Nullable<string>;
  otherPolicyType?: Nullable<string>;
  medicaidCoverage?: Nullable<boolean>;
  slmb?: Nullable<boolean>;
  qmb?: Nullable<boolean>;
  otherMedicaidBenefits?: Nullable<boolean>;
  statementToApplicant?: Nullable<string>;
}

export interface Insured {
  insuredType: 'PRIMARY' | 'SPOUSE' | 'CHILD';
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  email: Nullable<{
    email: string;
    type: 'PERS';
    preferred: boolean;
  }>;
  genderCd: null;
  relationshipToPrimaryInsuredCd: Nullable<'SPOUSE' | 'CHILD'>;
}

export interface Address {
  addressLine1: string;
  addressLine2?: string;
  city: string;
  stateCode: string;
  zipCode: string;
  country: 'US';
}

export type Agreement =
  'eConsent' | 'HIPAA' | 'JMA' | 'Insurance' | 'Terms and Conditions';

/**
 * A collections of agreements that user need to click through, it has state variation
 */
export interface Agreements {
  name: string;
  collections: any[];
  state?: string;
}

export type StateReplacementQuestionnaireData = {
  name?: string;
  title?: string;
  textBody?: Document;
  state?: string;
  products?: string[];
};

export type StateReplacementPdfItem = {
  fields: StateReplacementQuestionnaireData;
};

export type RetailDataIntegrationEntry = {
  fields: {
    cmsName?: string;
    id?: string;
  };
};

export type GetStateReplacementPdfContentResponse = {
  items: StateReplacementPdfItem[];
  includes?: {
    Entry?: RetailDataIntegrationEntry[];
  };
};
